import { useAuth } from './useAuth';

type UserRole = 'reader' | 'contributor' | 'editor' | 'admin' | 'super_admin';

interface RolePermissions {
  // Content permissions
  canViewContent: boolean;
  canCreateContent: boolean;
  canEditContent: boolean;
  canDeleteContent: boolean;
  canPublishContent: boolean;
  
  // User management permissions
  canManageUsers: boolean;
  canDeleteUsers: boolean;
  canResetUserInterests: boolean;
  canViewUserData: boolean;
  
  // System permissions
  canAccessAdminPanel: boolean;
  canViewAnalytics: boolean;
  canManageSystem: boolean;
  canViewLogs: boolean;
  
  // Navigation permissions
  canAccessDashboard: boolean;
  canAccessContentHub: boolean;
  canAccessEditorial: boolean;
  canAccessAdmin: boolean;
  
  // Features
  canUseAdvancedSearch: boolean;
  canExportData: boolean;
  canCreateCustomPaths: boolean;
}

const rolePermissions: Record<UserRole, RolePermissions> = {
  reader: {
    canViewContent: true,
    canCreateContent: false,
    canEditContent: false,
    canDeleteContent: false,
    canPublishContent: false,
    canManageUsers: false,
    canDeleteUsers: false,
    canResetUserInterests: false,
    canViewUserData: false,
    canAccessAdminPanel: false,
    canViewAnalytics: false,
    canManageSystem: false,
    canViewLogs: false,
    canAccessDashboard: true,
    canAccessContentHub: false,
    canAccessEditorial: false,
    canAccessAdmin: false,
    canUseAdvancedSearch: true,
    canExportData: false,
    canCreateCustomPaths: false,
  },
  contributor: {
    canViewContent: true,
    canCreateContent: true,
    canEditContent: false,
    canDeleteContent: false,
    canPublishContent: false,
    canManageUsers: false,
    canDeleteUsers: false,
    canResetUserInterests: false,
    canViewUserData: false,
    canAccessAdminPanel: false,
    canViewAnalytics: false,
    canManageSystem: false,
    canViewLogs: false,
    canAccessDashboard: true,
    canAccessContentHub: true,
    canAccessEditorial: false,
    canAccessAdmin: false,
    canUseAdvancedSearch: true,
    canExportData: true,
    canCreateCustomPaths: true,
  },
  editor: {
    canViewContent: true,
    canCreateContent: true,
    canEditContent: true,
    canDeleteContent: false,
    canPublishContent: true,
    canManageUsers: false,
    canDeleteUsers: false,
    canResetUserInterests: false,
    canViewUserData: false,
    canAccessAdminPanel: false,
    canViewAnalytics: true,
    canManageSystem: false,
    canViewLogs: false,
    canAccessDashboard: true,
    canAccessContentHub: true,
    canAccessEditorial: true,
    canAccessAdmin: false,
    canUseAdvancedSearch: true,
    canExportData: true,
    canCreateCustomPaths: true,
  },
  admin: {
    canViewContent: true,
    canCreateContent: true,
    canEditContent: true,
    canDeleteContent: true,
    canPublishContent: true,
    canManageUsers: true,
    canDeleteUsers: true,
    canResetUserInterests: true,
    canViewUserData: true,
    canAccessAdminPanel: true,
    canViewAnalytics: true,
    canManageSystem: true,
    canViewLogs: true,
    canAccessDashboard: true,
    canAccessContentHub: true,
    canAccessEditorial: true,
    canAccessAdmin: true,
    canUseAdvancedSearch: true,
    canExportData: true,
    canCreateCustomPaths: true,
  },
  super_admin: {
    canViewContent: true,
    canCreateContent: true,
    canEditContent: true,
    canDeleteContent: true,
    canPublishContent: true,
    canManageUsers: true,
    canDeleteUsers: true,
    canResetUserInterests: true,
    canViewUserData: true,
    canAccessAdminPanel: true,
    canViewAnalytics: true,
    canManageSystem: true,
    canViewLogs: true,
    canAccessDashboard: true,
    canAccessContentHub: true,
    canAccessEditorial: true,
    canAccessAdmin: true,
    canUseAdvancedSearch: true,
    canExportData: true,
    canCreateCustomPaths: true,
  },
};

export const useRoleAccess = () => {
  const { profile } = useAuth();
  const userRole = (profile?.role as UserRole) || 'reader';
  
  const permissions = rolePermissions[userRole];
  
  // Helper functions for common permission checks
  const hasRole = (role: UserRole | UserRole[]) => {
    if (Array.isArray(role)) {
      return role.includes(userRole);
    }
    return userRole === role;
  };
  
  const hasMinimumRole = (minimumRole: UserRole) => {
    const roleHierarchy: UserRole[] = ['reader', 'contributor', 'editor', 'admin', 'super_admin'];
    const userRoleIndex = roleHierarchy.indexOf(userRole);
    const minimumRoleIndex = roleHierarchy.indexOf(minimumRole);
    return userRoleIndex >= minimumRoleIndex;
  };
  
  // Navigation helpers
  const getAvailableNavItems = () => {
    const navItems = [];
    
    if (permissions.canAccessDashboard) {
      navItems.push({
        id: 'dashboard',
        label: 'Dashboard',
        path: '/dashboard',
        description: 'Your learning hub'
      });
    }
    
    if (permissions.canAccessContentHub) {
      navItems.push({
        id: 'content',
        label: 'Content Hub',
        path: '/content',
        description: 'Create and manage content'
      });
    }
    
    if (permissions.canAccessEditorial) {
      navItems.push({
        id: 'editorial',
        label: 'Editorial',
        path: '/editorial',
        description: 'Review and edit content'
      });
    }
    
    if (permissions.canAccessAdmin) {
      navItems.push({
        id: 'admin',
        label: 'Admin Portal',
        path: '/admin',
        description: 'System administration'
      });
    }
    
    return navItems;
  };
  
  const getDefaultLandingPage = () => {
    if (permissions.canAccessAdmin && hasMinimumRole('admin')) {
      return '/admin';
    }
    if (permissions.canAccessEditorial && hasMinimumRole('editor')) {
      return '/editorial';
    }
    if (permissions.canAccessContentHub && hasMinimumRole('contributor')) {
      return '/content';
    }
    return '/dashboard';
  };
  
  // Backwards compatibility (keeping existing function names)
  const canCreateContent = () => permissions.canCreateContent;
  const canEditContent = () => permissions.canEditContent;
  const canManageUsers = () => permissions.canManageUsers;
  
  return {
    // Role information
    userRole,
    hasRole,
    hasMinimumRole,
    
    // All permissions
    ...permissions,
    
    // Navigation helpers
    getAvailableNavItems,
    getDefaultLandingPage,
    
    // Backwards compatibility
    canCreateContent,
    canEditContent,
    canManageUsers,
  };
}; 